import type { UseQueryResult } from "@tanstack/react-query";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import type { DateRange } from "react-day-picker";

import { Badge, Button, DateRangePicker, ErrorContainer, FormInput, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Table, TableActionsDropdown } from "@sentricmusic/ui";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { GigClaim } from "@/api/queries";

import type { StatusOption } from "../utils/gigsFilterUtils";

import { GIGS_STATUS, STATUS_OPTIONS } from "../utils/gigsClaimConstants";
import { isStatusOption, matchesStatus } from "../utils/gigsFilterUtils";

type AllGigsProps = {
  gigClaims: UseQueryResult<GigClaim[], Error>;
  adminRights: boolean;
};

function AllGigs({ gigClaims, adminRights }: AllGigsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [venue, setVenue] = useState("");
  const [status, setStatus] = useState<StatusOption>("all");
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  // sort by most recent gig claim submissions
  const [sorting, setSorting] = useState<SortingState>([{ id: "created", desc: true }]);

  const handleClear = () => {
    setVenue("");
    setStatus("all");
    setDateRange(undefined);
  };

  const columns = useMemo((): ColumnDef<GigClaim>[] => [
    { accessorKey: "venueName", header: String(t("dashboard:ra-column-venue", "Venue")), size: 300 },
    {
      accessorKey: "date",
      header: String(t("dashboard:ra-column-date", "Date")),
      size: 120,
      cell: ({ row }) => {
        const date = row.original.date;
        return date !== "" && date !== "0000-00-00" ? date.split("T")[0] : "Unknown date";
      },
    },
    { accessorKey: "time", header: String(t("gigs:table-header-time", "Time")), size: 120 },
    { accessorKey: "setlistName", header: String(t("gigs:table-header-setlist", "Setlist")), size: 140 },
    {
      accessorKey: "gigStatus",
      id: "statusPill",
      header: String(t("songs:song-table-status", "Status")),
      size: 160,
      cell: ({ row }) => {
        switch (row.original.gigStatus) {
          case GIGS_STATUS.NOT_VERIFIED:
            return <Badge variant="warning">{String(t("gigs:pill-not-verified"))}</Badge>;
          case GIGS_STATUS.PROCESSED_PENDING:
          case GIGS_STATUS.PROCESSED_COMPLETE:
          case GIGS_STATUS.PROCESSED_PARTIAL:
            return <Badge variant="success">{String(t("gigs:pill-processed"))}</Badge>;
          case GIGS_STATUS.INELIGIBLE:
            return <Badge variant="danger">{String(t("gigs:pill-ineligible"))}</Badge>;
          case GIGS_STATUS.REJECTED:
            return <Badge variant="danger">{String(t("gigs:pill-rejected"))}</Badge>;
          case GIGS_STATUS.ARCHIVED:
            return <Badge variant="success">{String(t("gigs:pill-archived"))}</Badge>;
          case undefined:
          default:
            return null;
        }
      },
    },
    {
      accessorKey: "created",
      header: String(t("gigs:table-header-claim-submitted", "Claim Submitted")),
      size: 160,
      cell: ({ row }) => {
        const createdAt = row.original.created;
        return (createdAt != null) && createdAt !== "" && createdAt !== "0000-00-00"
          ? createdAt.split("T")[0]
          : "—";
      },
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      size: 60,
      minSize: 60,
      maxSize: 60,
      enablePinning: true,
      meta: { pinSide: "right" },
      cell: ({ row }) => {
        const canAccess = row.original.gigStatus === GIGS_STATUS.NOT_VERIFIED || adminRights;
        const goToGig = (event: React.MouseEvent): void => {
          event.stopPropagation();
          if (canAccess) {
            void navigate({ to: `/gigs/claim/update/${row.original.id}` });
          }
        };

        return (
          <TableActionsDropdown disabled={!canAccess}>
            <Button
              variant="dropdown"
              label={String(t("common:edit", "Edit"))}
              onClick={goToGig}
            />
          </TableActionsDropdown>
        );
      },
    },
  ], [t, adminRights, navigate]);

  const filteredGigs = useMemo(() => {
    const gigs = gigClaims.data ?? [];

    const venueTerm = venue.trim().toLowerCase();
    const fromStr = dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : undefined;
    const toStr = dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : undefined;

    return gigs.filter((gig) => {
      if (venueTerm !== "" && !gig.venueName?.toLowerCase().includes(venueTerm)) {
        return false;
      }

      if (!matchesStatus(gig.gigStatus, status)) {
        return false;
      }

      if (fromStr !== undefined || toStr !== undefined) {
        const gigDate = (gig.date !== "" && gig.date !== "0000-00-00")
          ? gig.date.split("T")[0]
          : null;
        if (gigDate === null)
          return false;
        if (fromStr !== undefined && gigDate < fromStr)
          return false;
        if (toStr !== undefined && gigDate > toStr)
          return false;
      }

      return true;
    });
  }, [gigClaims.data, venue, status, dateRange]);

  const filterContent = (
    <>
      <FormInput
        smallFont
        label={t("gigs:filter-by-venue", "Venue")}
        placeholder={`${t("common:filter", "Filter")} ${t("common:gigs", "gigs")}`}
        value={venue}
        onChange={e => setVenue(e.target.value)}
      />
      <Select value={status} onValueChange={v => setStatus(isStatusOption(v) ? v : "all")}>
        <SelectTrigger smallFont label={t("gigs:filter-by-status", "Status")} className="pl-0 w-full">
          <SelectValue placeholder={t("gigs:filter-status-all", "All")} />
        </SelectTrigger>
        <SelectContent>
          {STATUS_OPTIONS.map(opt => (
            <SelectItem key={opt.value} value={opt.value}>
              {t(opt.labelKey, opt.labelDefault)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <DateRangePicker
        smallFont
        label={t("gigs:filter-by-date-range", "Date range")}
        initialRange={dateRange}
        onSelect={setDateRange}
        maxDate={new Date()}
        numberOfMonths={1}
      />
    </>
  );

  const totalGigs = gigClaims.data?.length ?? 0;
  const filteredCount = filteredGigs.length;

  const statsBlock = useMemo(() => [
    {
      title: t("gigs:stat-total-gigs", "Total gigs"),
      value: totalGigs,
      highlight: false,
    },
    {
      title: t("gigs:stat-filtered-gigs", "Filtered"),
      value: filteredCount,
      highlight: true,
    },
  ], [totalGigs, filteredCount, t]);

  return (
    <div className="overflow-x-auto">
      {gigClaims.isError
        ? (
            <ErrorContainer message={gigClaims.error?.message ?? t("common:errorGettingRecentGigs", "Error getting recent gigs")} />
          )
        : (
            <Table
              enableColumnResizing={false}
              sorting={sorting}
              onSortingChange={setSorting}
              data={filteredGigs}
              columns={columns}
              filterContent={filterContent}
              onClear={handleClear}
              statsBlock={statsBlock}
              isLoading={gigClaims.isLoading}
              isRefetchingData={gigClaims.isFetching}
              defaultPageSize={10}
            />
          )}
    </div>
  );
}

export default AllGigs;
